import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Css, ITheme, Responsive, ResponsivePicture } from '../../../ui';

export type HeaderImageVariants =
	| 'contentPage'
	| 'landingPage'
	| 'newsroomNewsPage';

interface IHeaderImageProps {
	variant?: HeaderImageVariants;
	src: string;
	imageAlt: string;
}

export default function HeaderImage({
	variant = 'contentPage',
	src,
	imageAlt,
}: IHeaderImageProps) {
	return (
		<HeaderImageWrapper>
			<StyledImage source={src} alt={imageAlt} variant={variant} />
		</HeaderImageWrapper>
	);
}

const HeaderImageWrapper = styled.div`
	display: flex;
	margin-right: -1em;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		justify-content: flex-end;
	}
`;

const contentPageCss = css`
	width: 720px;
	aspect-ratio: 2.4 / 1;
	height: 336px;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		width: 100%;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		width: 100%;
		height: 200px;
	}
`;

const landingPageCss = css`
	width: 497px;
	aspect-ratio: 1 / 1.11;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		width: 100%;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		width: 100%;
		height: 205px;
	}
`;

const newsroomNewsPageCss = css`
	width: 720px;
	aspect-ratio: 3 / 1;
	height: 302px;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		width: 100%;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		width: 100%;
		height: 200px;
	}
`;

const StyledImage = styled(ResponsivePicture)<
	{ variant?: IHeaderImageProps['variant'] },
	ITheme
>`
	position: relative;
	display: flex;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		width: 92%;
	}

	img {
		border-radius: ${(props) => props.theme.layout.image.borderRadius};
		position: relative;
		object-fit: cover;
		object-position: center;

		@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
			border-radius: ${(props) => props.theme.layout.image.borderRadius} 0 0
				${(props) => props.theme.layout.image.borderRadius};
		}

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			border-radius: ${(props) => props.theme.layout.image.mobile.borderRadius}
				0 0 ${(props) => props.theme.layout.image.mobile.borderRadius};
		}

		${({ variant }) =>
			variant === 'contentPage'
				? contentPageCss
				: variant === 'landingPage'
				? landingPageCss
				: variant === 'newsroomNewsPage'
				? newsroomNewsPageCss
				: ``}
	}

	&::before {
		content: '';
		position: absolute;
		bottom: ${Css.pxToRem(-24)};
		left: ${Css.pxToRem(-24)};
		width: 100%;
		height: ${({ variant }) => imageHeights[variant!] || '0%'};
		border-radius: ${(props) => props.theme.layout.image.borderRadius};
		background-color: ${(props) => props.theme.color.brand};

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			bottom: ${({ variant }) =>
				variant === 'landingPage' ? Css.pxToRem(-16) : Css.pxToRem(-8)};
			left: ${({ variant }) =>
				variant === 'landingPage' ? Css.pxToRem(-16) : Css.pxToRem(-8)};
			border-radius: ${(props) => props.theme.layout.image.mobile.borderRadius};
			width: 90%;
		}
	}
`;

const imageHeights = {
	contentPage: '96%',
	landingPage: '76%',
	newsroomNewsPage: '96%',
};
